import {
  isGenetecDirectSite,
  SiteLike,
} from "../pages/EditCamera/EditCameraUtils/genetecVideoSource";

/**
 * Who sees ONVIF Scan, and where (ENG-1922, from SUP-615).
 *
 * The scan runs on the site's WireGuard gateway through actuate_admin's
 * `network-auto-discovery/`, whose permission is reported on `/user/info/` as
 * `configure_wireguard_cameras` (WireGuard admin feature on the group plus
 * add and edit cameras). The button used to require `super_user`, so no
 * customer could ever see it. Superusers keep access unchanged.
 *
 * The scan saves discovered cameras with the site's own camera template, and
 * the fields it fills match the `rtsp` template, so it is offered only on
 * `rtsp` and `onvif_wireguard` sites. Anything else (Milestone, AILink,
 * autopatrol, …) would save cameras with the wrong template.
 */
export const ONVIF_SCAN_INTEGRATIONS = ["rtsp", "onvif_wireguard"];

type Permissions =
  | {
      add_cameras?: boolean;
      super_user?: boolean;
      configure_wireguard_cameras?: boolean;
    }
  | null
  | undefined;

/** May this user run the scan at all (routes)? */
export const canUseOnvifScan = (permissions: Permissions): boolean =>
  Boolean(permissions?.add_cameras) &&
  Boolean(permissions?.configure_wireguard_cameras || permissions?.super_user);

/** Should the ONVIF Scan button show on this site (buttons)? */
export const canShowOnvifScan = (
  permissions: Permissions,
  integrationTypeName: string | null | undefined
): boolean =>
  canUseOnvifScan(permissions) &&
  ONVIF_SCAN_INTEGRATIONS.includes(String(integrationTypeName ?? ""));

// ---------------------------------------------------------------------------
// "Refresh camera streams" — the update mode of the scan page.
//
// Re-reads the unit behind every existing camera that has a host over ONVIF
// (Camera Admin `camera-unit-scan/`) and rewrites host, streaming port, stream
// path and login in place. It never creates cameras, so it is gated on
// edit_cameras plus the direct-camera-access permission (or super_user) — not
// on add_cameras / configure_wireguard_cameras — and it needs no WireGuard
// tunnel: the first users are Genetec sites with video source = direct,
// reached over an IPsec VPN.
// ---------------------------------------------------------------------------

type UnitScanPermissions =
  | {
      edit_cameras?: boolean;
      super_user?: boolean;
      direct_camera_access?: boolean;
    }
  | null
  | undefined;

/** May this user run the camera unit scan at all (routes)? */
export const canUseCameraUnitScan = (
  permissions: UnitScanPermissions
): boolean =>
  Boolean(permissions?.edit_cameras) &&
  Boolean(permissions?.direct_camera_access || permissions?.super_user);

/**
 * Should the "Refresh camera streams" button show on this site (buttons)?
 * Genetec sites only when their video source is direct (the connector dials
 * the units itself); rtsp / onvif_wireguard sites always carry a unit host.
 */
export const canShowCameraUnitScan = (
  permissions: UnitScanPermissions,
  siteInfo: SiteLike
): boolean =>
  canUseCameraUnitScan(permissions) &&
  (isGenetecDirectSite(siteInfo) ||
    ONVIF_SCAN_INTEGRATIONS.includes(
      String(siteInfo?.integration_type_name ?? "")
    ));
